import { Spinner } from "@/components/ui/spinner";
import { useLanguage } from "@/hooks/useLanguage";
import { isErrorCode } from "@/lib/error-messages";
import { resolveFlexSiteUrl } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  selectFetchedOrderStatus,
  selectInitComplete,
} from "@/store/selectors/order.selector";
import { setInitializeComplete } from "@/store/slices/order.slice";
import { fetchOrder } from "@/store/thunks/order.thunks";
import { trackBeginCheckout } from "@/lib/analytics/ga4";
import { getPartnerAttribution, setPartnerAttribution, trackAttributionInPostHog } from "@/utils/partnerAttribution";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import CheckoutError from "./checkout/checkout-error";

interface AppInitializerProps {
  children: React.ReactNode;
}

export function AppInitializer({ children }: AppInitializerProps) {
  const language = useLanguage();
  const { t, i18n } = useTranslation();
  const { orderId } = useParams();
  const dispatch = useAppDispatch();
  const initCompleted = useAppSelector(selectInitComplete);
  const error = useSelector(selectFetchedOrderStatus).error;

  useEffect(() => {
    const initialize = async () => {
      // Check for partner attribution in URL params and set cookie
      const searchParams = new URLSearchParams(window.location.search);
      const refParam = searchParams.get('ref');
      const campaignParam = searchParams.get('campaign');
      const mediumParam = searchParams.get('utm_medium');

      if (refParam) {
        const success = setPartnerAttribution(refParam, {
          source: window.location.pathname,
          campaign: campaignParam || undefined,
          medium: mediumParam || undefined
        });

        if (success) {
          console.log('[Partner Attribution] Set from URL in checkout');
          // Clean URL by removing ref params
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('ref');
          cleanUrl.searchParams.delete('campaign');
          window.history.replaceState({}, '', cleanUrl.toString());
        }
      }

      if (!orderId) {
        dispatch(setInitializeComplete());
        return;
      }
      try {
        const orderResult = await dispatch(fetchOrder(orderId)).unwrap();

        if (orderResult.metadata?.["checkout_completed"] === "true") {
          window.location.href = `${resolveFlexSiteUrl("/order-mottagen", language)}?orderId=${
            orderResult.id
          }`;
          return;
        }

        trackBeginCheckout(orderResult);

        // Track partner attribution if present
        const attribution = getPartnerAttribution();
        if (attribution) {
          console.log('[Partner Attribution] Detected in checkout:', attribution);
          trackAttributionInPostHog(attribution);

          // Log if attribution is already in order metadata
          if (orderResult.metadata?.partner_ref) {
            console.log('[Partner Attribution] Already in order metadata:', {
              partner: orderResult.metadata.partner_ref,
              source: orderResult.metadata.partner_source,
              age_minutes: orderResult.metadata.partner_age_minutes
            });
          }
        }

        dispatch(setInitializeComplete());
      } catch (error) {
        console.error("Failed to initialize app:", error);
        dispatch(setInitializeComplete());
      }
    };

    initialize();
  }, [dispatch, orderId, language]);

  if (!initCompleted || !i18n.isInitialized) {
    return <Spinner fullScreen message={t("initialLoading")} />;
  }

  if (!orderId) {
    return (
      <CheckoutError
        title={t("errors.title")}
        description={t("errors.noOrder")}
      />
    );
  }

  if (error) {
    const errorMessage =
      error && isErrorCode(error) ? t(`errors.${error}`) : t("errors.generic");

    return (
      <CheckoutError title={t("errors.title")} description={errorMessage} />
    );
  }

  return <>{children}</>;
}
